import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Globe, Users, Tv, Brain, Heart, Zap, BookOpen, Video } from "lucide-react";
import { Helmet } from "react-helmet-async";

const tocItems = [
  { id: "what-is-gongbang", title: "What is Gongbang?" },
  { id: "korean-origins", title: "The Korean Origins" },
  { id: "global-expansion", title: "From Seoul to the World" },
  { id: "psychology-behind", title: "Why It Works: The Psychology" },
  { id: "key-players", title: "Pioneers and Platforms" },
  { id: "csw-role", title: "How CSW Fits In" },
  { id: "future", title: "The Future of Study Streaming" },
  { id: "references", title: "References" },
];

const GongbangToGlobal = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Gongbang?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Gongbang (공방) is a Korean portmanteau combining 'gongbu' (공부, meaning study) and 'bangsong' (방송, meaning broadcast). It refers to live streams where people film themselves studying, allowing viewers to study alongside them virtually."
        }
      },
      {
        "@type": "Question",
        "name": "Where did Gongbang originate?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Gongbang originated in South Korea around 2018, driven by the country's intense academic culture and the rise of live streaming platforms. Korean students began broadcasting their study sessions to stay motivated and accountable."
        }
      },
      {
        "@type": "Question",
        "name": "How did study streaming spread globally?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Study streaming spread from Korea to platforms like YouTube and Twitch, reaching millions of students worldwide. The COVID-19 pandemic accelerated adoption as students sought virtual study companions during lockdowns."
        }
      },
      {
        "@type": "Question",
        "name": "Why does watching someone study help you focus?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Watching others study triggers social facilitation and provides 'positive peer pressure.' The visible presence of focused individuals activates social modeling, making your brain more likely to stay on task."
        }
      },
      {
        "@type": "Question",
        "name": "What is the difference between Gongbang and Study With Me?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Gongbang refers specifically to the Korean study streaming phenomenon, while 'Study With Me' is the broader English term used globally. Both describe the same concept of broadcasting or recording study sessions for others to watch."
        }
      },
      {
        "@type": "Question",
        "name": "Who started the Gongbang trend?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "While many Korean students contributed to the trend, creators like 'The Strive Studies' and various Korean YouTubers helped popularize the format. The trend grew organically from Korea's competitive exam culture."
        }
      },
      {
        "@type": "Question",
        "name": "What platforms are best for study streaming?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Popular platforms include YouTube for recorded Study With Me videos, Twitch for live streams, and dedicated platforms like CSW.live, StudyStream, and Focusmate for interactive virtual co-working."
        }
      },
      {
        "@type": "Question",
        "name": "Is study streaming effective for productivity?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, research shows that virtual co-presence improves focus and reduces procrastination. Studies demonstrate 16-32% productivity improvements when working alongside peers, even virtually."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "From Gongbang to Global: The Worldwide Rise of Study Stream Culture",
    "description": "Explore how Korean study streaming culture spread worldwide, transforming how millions of students study. Learn about the origins, psychology, and future of the Gongbang phenomenon.",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-17",
    "dateModified": "2025-01-17",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/study-stream/gongbang-to-global/"
    },
    "keywords": ["Gongbang", "공방", "study streaming", "Study With Me", "Korean study culture", "virtual study room", "online study", "focus productivity", "study stream"],
    "about": [
      { "@type": "Thing", "name": "Gongbang" },
      { "@type": "Thing", "name": "Study Streaming" },
      { "@type": "Thing", "name": "Korean Study Culture" },
      { "@type": "Thing", "name": "Virtual Co-working" },
      { "@type": "Thing", "name": "Study Productivity" }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Study Stream",
        "item": "https://csw.live/study-stream/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "From Gongbang to Global",
        "item": "https://csw.live/study-stream/gongbang-to-global/"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <SEO 
        title="From Gongbang to Global: The Worldwide Rise of Study Stream Culture"
        description="Explore how Korean study streaming culture spread worldwide, transforming how millions of students study. Learn about the origins, psychology, and future of the Gongbang phenomenon."
        canonicalUrl="/study-stream/gongbang-to-global/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/study-stream/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Study Stream
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                Culture
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>January 2025</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              From Gongbang to Global: The Worldwide Rise of Study Stream Culture
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="flex gap-12 max-w-6xl mx-auto">
            {/* Main Content */}
            <div className="flex-1 max-w-4xl">
              {/* Table of Contents - Mobile */}
              <div className="lg:hidden mb-12">
                <BlogTableOfContents items={tocItems} />
              </div>

              {/* Lead Paragraph */}
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                What started as a niche phenomenon in South Korean internet culture has become a global movement. Millions of students worldwide now study alongside strangers on camera—and it's changing how we think about productivity, loneliness, and the simple act of sitting down to work.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Welcome to the world of <strong>Gongbang</strong> (공방)—a uniquely Korean invention that has quietly revolutionized how a generation learns.
              </p>

              {/* Section 1: What is Gongbang */}
              <div id="what-is-gongbang" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Tv className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      What is Gongbang?
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                <strong>Gongbang</strong> is a Korean portmanteau combining two words:
              </p>

              <ul className="list-disc list-inside space-y-2 text-lg font-body text-foreground/90 mb-8 ml-4">
                <li><strong>Gongbu</strong> (공부) — meaning "study"</li>
                <li><strong>Bangsong</strong> (방송) — meaning "broadcast"</li>
              </ul>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Put together, Gongbang refers to <strong>study broadcasts</strong>—live streams where people film themselves studying, often in silence, for hours at a time. Viewers tune in not to watch entertainment, but to study alongside the streamer.
              </p>

              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-10">
                <div className="flex items-start gap-4">
                  <Video className="w-8 h-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-display font-bold text-lg mb-2">The Gongbang Format</h3>
                    <p className="text-muted-foreground font-body">
                      A typical Gongbang stream shows a student at their desk, often with a timer visible, ambient lighting, and occasionally ASMR-style sounds like keyboard clicks or rain. The streamer studies silently while viewers do the same—creating a virtual study room.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                In English-speaking countries, this concept became known as <strong>"Study With Me"</strong> videos—but the roots trace directly back to Korea's intensely competitive academic culture.
              </p>

              {/* Section 2: Korean Origins */}
              <div id="korean-origins" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      The Korean Origins
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                South Korea has one of the most competitive education systems in the world. The <strong>Suneung</strong> (수능)—the national college entrance exam—determines students' futures so profoundly that the entire country adjusts its schedule on test day: flights are grounded, businesses open late, and police escorts are provided for students running late.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                In this pressure-cooker environment, students spend enormous hours studying—often 12 to 16 hours daily during exam periods. But studying alone at home presented a problem: <strong>isolation and motivation.</strong>
              </p>

              <div className="bg-muted/50 rounded-xl p-6 mb-8">
                <p className="text-lg font-body text-foreground/90 italic text-center">
                  "I felt so alone studying at home. When I turned on a Gongbang stream, it was like having a study partner—even though we never spoke."
                </p>
                <p className="text-sm text-muted-foreground text-center mt-2">— Korean university student, interviewed by The Guardian</p>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Around <strong>2018</strong>, Korean students began broadcasting their study sessions on platforms like AfreecaTV and YouTube. The format was simple: set up a camera, hit record, and study. Viewers could watch in real-time, creating a sense of <strong>shared presence</strong> despite physical distance (
                <a 
                  href="https://www.theguardian.com/world/2018/oct/09/watch-me-study-south-koreas-online-exam-live-streams" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  The Guardian, 2018
                </a>
                ).
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                What surprised everyone was how quickly this format caught on—not just in Korea, but around the world.
              </p>

              {/* Section 3: Global Expansion */}
              <div id="global-expansion" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      From Seoul to the World
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                By 2019, "Study With Me" videos had exploded on YouTube. Creators from the US, UK, Japan, and across Europe began producing their own versions—often citing Korean Gongbang as their inspiration.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Then came <strong>2020</strong>. The COVID-19 pandemic forced students worldwide into isolation. Libraries closed. Study groups dissolved. Suddenly, millions of students understood what Korean students had discovered years earlier: <strong>studying alone is hard.</strong>
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-10">
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="text-3xl font-display font-bold text-primary mb-2">20M+</div>
                  <p className="text-sm text-muted-foreground font-body">Views on top Study With Me videos</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="text-3xl font-display font-bold text-primary mb-2">50+</div>
                  <p className="text-sm text-muted-foreground font-body">Countries with active study streamers</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="text-3xl font-display font-bold text-primary mb-2">24/7</div>
                  <p className="text-sm text-muted-foreground font-body">Continuous study streams available</p>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Platforms like <strong>YouTube</strong> and <strong>Twitch</strong> saw massive growth in study content. Dedicated apps and websites emerged to serve this new demand. What had been a Korean niche became a <strong>global phenomenon</strong> (
                <a 
                  href="https://www.vice.com/en/article/watching-strangers-study-online-helps-me-study-too/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Vice, 2020
                </a>
                ).
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                The pandemic didn't create study streaming—but it proved that the need for virtual study companionship is universal, not uniquely Korean.
              </p>

              {/* Section 4: Psychology Behind It */}
              <div id="psychology-behind" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      Why It Works: The Psychology
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                The success of Gongbang isn't accidental. It taps into several well-documented psychological mechanisms:
              </p>

              <div className="space-y-6 mb-10">
                <div className="bg-muted/30 rounded-xl p-6">
                  <h3 className="font-display font-bold text-lg mb-2">1. Camaraderie in Solitude</h3>
                  <p className="text-muted-foreground font-body">
                    Humans are social creatures. Even passive social presence—knowing someone else is there—reduces feelings of loneliness and increases motivation. Study streaming provides companionship without the distraction of conversation.
                  </p>
                </div>

                <div className="bg-muted/30 rounded-xl p-6">
                  <h3 className="font-display font-bold text-lg mb-2">2. Positive Peer Pressure</h3>
                  <p className="text-muted-foreground font-body">
                    Seeing others study creates subtle accountability. You're less likely to check your phone when everyone on screen is focused. This is <Link to="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline">social facilitation</Link> in action—performing better when others are doing the same task.
                  </p>
                </div>

                <div className="bg-muted/30 rounded-xl p-6">
                  <h3 className="font-display font-bold text-lg mb-2">3. Structured Focus</h3>
                  <p className="text-muted-foreground font-body">
                    Many Gongbang streams incorporate Pomodoro timers—25 minutes of work, 5 minutes of rest. This external structure helps viewers maintain focus without having to manage their own timers.
                  </p>
                </div>

                <div className="bg-muted/30 rounded-xl p-6">
                  <h3 className="font-display font-bold text-lg mb-2">4. Social Modeling</h3>
                  <p className="text-muted-foreground font-body">
                    Your brain is wired to mirror behaviors you observe. Watching someone study intently makes your brain more likely to enter a similar focused state—a phenomenon called <strong>social modeling</strong>.
                  </p>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                Research on Study With Me videos confirms these effects. A <a href="https://doi.org/10.1016/j.compedu.2025.105488" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">2025 study</a> found that students studying with virtual companions reported improved concentration and longer focus periods—matching findings on <Link to="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline">camera-on studying</Link>.
              </p>

              {/* Section 5: Key Players */}
              <div id="key-players" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      Pioneers and Platforms
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                The Gongbang movement owes its success to both individual creators and dedicated platforms:
              </p>

              <h3 className="font-display font-bold text-xl mb-4">Notable Creators</h3>
              <ul className="list-disc list-inside space-y-2 text-lg font-body text-foreground/90 mb-8 ml-4">
                <li><strong>The Strive Studies</strong> — One of the most popular English-language Study With Me channels</li>
                <li><strong>James Scholz</strong> — Known for 10,000+ hour study streams</li>
                <li><strong>Korean BJ streamers</strong> — Original Gongbang pioneers on AfreecaTV</li>
                <li><strong>Merve</strong> — Popular Turkish study streamer with millions of views</li>
              </ul>

              <h3 className="font-display font-bold text-xl mb-4">Dedicated Platforms</h3>
              <ul className="list-disc list-inside space-y-2 text-lg font-body text-foreground/90 mb-8 ml-4">
                <li><strong>열품타 (YPT/YeolPumTa)</strong> — Korea's most popular study app with 3+ million downloads</li>
                <li><strong>StudyStream</strong> — Discord-based study community</li>
                <li><strong>Focusmate</strong> — Matched 50-minute video sessions</li>
                <li><strong>CSW.live</strong> — Camera-on study rooms with real students worldwide</li>
              </ul>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                What's remarkable is how the format has stayed consistent across cultures: a desk, a camera, silence, and focus. The simplicity is part of the appeal—no production value needed, just genuine studying.
              </p>

              {/* Section 6: CSW Role */}
              <div id="csw-role" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      How CSW Fits In
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                <strong>CSW</strong> (Collaborative Study Workspace) takes the Gongbang concept and makes it interactive. Instead of passively watching a streamer, you join a <strong>live study room</strong> with real students studying at the same time.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-10">
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-6">
                  <h4 className="font-display font-bold mb-2">Traditional Gongbang</h4>
                  <ul className="text-sm text-muted-foreground font-body space-y-1">
                    <li>• Watch a streamer study</li>
                    <li>• One-way experience</li>
                    <li>• No accountability</li>
                    <li>• Scheduled streams only</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-6">
                  <h4 className="font-display font-bold mb-2">CSW Study Rooms</h4>
                  <ul className="text-sm text-muted-foreground font-body space-y-1">
                    <li>• Study with real peers</li>
                    <li>• Two-way presence</li>
                    <li>• Camera-on accountability</li>
                    <li>• Available 24/7</li>
                  </ul>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                CSW combines the <Link to="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline">psychological benefits of camera-on studying</Link> with the communal spirit of Gongbang—creating a global virtual library that never closes.
              </p>

              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-12">
                <div className="flex items-start gap-4">
                  <Heart className="w-8 h-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-display font-bold text-lg mb-2">The CSW Difference</h3>
                    <p className="text-muted-foreground font-body">
                      Unlike recorded Study With Me videos, CSW connects you with <strong>live students</strong> from around the world. When you turn on your camera, others can see you—creating mutual accountability that passive viewing can't replicate.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 7: The Future */}
              <div id="future" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      The Future of Study Streaming
                    </h2>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                The Gongbang phenomenon isn't slowing down. As remote work and online education become permanent fixtures of modern life, the demand for virtual co-presence will only grow.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                Emerging trends include:
              </p>

              <ul className="list-disc list-inside space-y-2 text-lg font-body text-foreground/90 mb-8 ml-4">
                <li><strong>AI study companions</strong> — Virtual avatars that provide presence without requiring human streamers</li>
                <li><strong>VR study rooms</strong> — Immersive virtual libraries and study spaces</li>
                <li><strong>Gamification</strong> — Earning points and achievements for focused study time</li>
                <li><strong>Global matching</strong> — Connecting students across time zones for 24/7 study availability</li>
              </ul>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                What started as Korean students filming themselves studying has become a <strong>global reimagining</strong> of how humans can work together—even when apart.
              </p>

              <div className="bg-muted/50 rounded-xl p-6 mb-12">
                <p className="text-lg font-body text-foreground/90 italic text-center">
                  "Gongbang proved something important: we don't need to be in the same room to study together. We just need to be present for each other."
                </p>
              </div>

              {/* References */}
              <div id="references" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                      References
                    </h2>
                  </div>
                </div>
              </div>

              <ul className="space-y-4 text-base font-body text-foreground/90">
                <li>
                  <a 
                    href="https://www.theguardian.com/world/2018/oct/09/watch-me-study-south-koreas-online-exam-live-streams" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    The Guardian (2018). "Watch me study: South Korea's online exam live streams"
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.vice.com/en/article/watching-strangers-study-online-helps-me-study-too/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Vice (2020). "Watching Strangers Study Online Helps Me Study Too"
                  </a>
                </li>
                <li>
                  <a 
                    href="https://doi.org/10.1016/j.compedu.2025.105488" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Liu, Deng & Zhou (2025). "Study With Me videos and student concentration"
                  </a>
                </li>
                <li>
                  <a 
                    href="https://doi.org/10.1016/j.compedu.2024.105243" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Computers & Education (2024). "Virtual study companions and academic performance"
                  </a>
                </li>
                <li>
                  <Link 
                    to="/study-stream/why-studying-on-camera-helps/"
                    className="text-primary hover:underline"
                  >
                    CSW Research: Why Studying on Camera Actually Helps You Get More Done
                  </Link>
                </li>
              </ul>

              {/* CTA Section */}
              <div className="mt-16 bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/20 rounded-3xl p-8 md:p-12 text-center">
                <h3 className="text-2xl md:text-3xl font-display font-bold mb-4">
                  Ready to Join the Global Study Movement?
                </h3>
                <p className="text-lg text-muted-foreground font-body mb-8 max-w-2xl mx-auto">
                  Experience the power of Gongbang with real students worldwide. No downloads, no accounts—just turn on your camera and study.
                </p>
                <a
                  href="https://csw.live"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Start Studying Now
                  <ArrowLeft className="w-4 h-4 rotate-180" />
                </a>
              </div>
            </div>

            {/* Table of Contents - Desktop Sidebar */}
            <div className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-32">
                <BlogTableOfContents items={tocItems} />
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default GongbangToGlobal;
